import React, { useState } from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { SalesForm } from '../../components/SalesForm';
import { Truck, Package, BarChart3, Clock, Users, CheckCircle, ArrowRight, Bot } from 'lucide-react';
export function FreightSolutionPage() {
  const [isSalesFormOpen, setIsSalesFormOpen] = useState(false);
  return <div className="flex flex-col min-h-screen w-full" data-id="element-1076">
      <Header data-id="element-1077" />
      <main className="flex-grow" data-id="element-1078">
        {/* Hero Section */}
        <div className="bg-black text-white py-16 md:py-24" data-id="element-1079">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1080">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-1081">
              <div data-id="element-1082">
                <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1083">
                  AI Solutions for Freight Companies
                </h1>
                <p className="text-xl text-gray-300 mb-8" data-id="element-1084">
                  Transform your freight operations with intelligent AI agents
                  that automate logistics, improve tracking, and enhance
                  customer communication.
                </p>
                <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-1085">
                  Request a Demo
                </button>
              </div>
              <div className="relative" data-id="element-1086">
                <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" alt="Freight logistics" className="rounded-lg shadow-lg" data-id="element-1087" />
                <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg" data-id="element-1088">
                  <Truck className="text-black" size={32} data-id="element-1089" />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Key Benefits */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1090">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1091">
            <div className="text-center mb-16" data-id="element-1092">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1093">
                Key Benefits for Freight Companies
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1094">
                Our AI agents are specifically designed to address the unique
                challenges faced by freight and logistics companies.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1095">
              <BenefitCard icon={<Package size={24} data-id="element-1097" />} title="Automated Shipment Tracking" description="AI agents monitor shipments in real-time, providing automated updates to customers and internal teams." data-id="element-1096" />
              <BenefitCard icon={<BarChart3 size={24} data-id="element-1099" />} title="Optimized Route Planning" description="Intelligent algorithms analyze traffic, weather, and historical data to recommend the most efficient routes." data-id="element-1098" />
              <BenefitCard icon={<Clock size={24} data-id="element-1101" />} title="Streamlined Documentation" description="Automate the processing of bills of lading, customs forms, and other essential freight documentation." data-id="element-1100" />
              <BenefitCard icon={<Users size={24} data-id="element-1103" />} title="Enhanced Customer Service" description="24/7 automated responses to common customer inquiries about shipment status and delivery estimates." data-id="element-1102" />
              <BenefitCard icon={<CheckCircle size={24} data-id="element-1105" />} title="Compliance Management" description="Stay on top of regulatory requirements with automated compliance checks and documentation." data-id="element-1104" />
              <BenefitCard icon={<Bot size={24} data-id="element-1107" />} title="Predictive Maintenance" description="AI agents monitor vehicle data to predict maintenance needs before they cause costly breakdowns." data-id="element-1106" />
            </div>
          </div>
        </div>
        {/* How It Works */}
        <div className="py-16 md:py-24 bg-gray-50" data-id="element-1108">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1109">
            <div className="text-center mb-16" data-id="element-1110">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1111">How It Works</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1112">
                Implementing Agentter.ai in your freight operations is simple
                and straightforward.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1113">
              <StepCard number="1" title="Connect Your Systems" description="Integrate with your existing TMS, ERP, and other logistics software with our no-code connectors." data-id="element-1114" />
              <StepCard number="2" title="Configure Your Agents" description="Define the tasks you want to automate using natural language - no coding required." data-id="element-1115" />
              <StepCard number="3" title="Deploy and Scale" description="Launch your AI agents and easily scale as your business grows and needs evolve." data-id="element-1116" />
            </div>
          </div>
        </div>
        {/* Features */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1117">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1118">
            <div className="text-center mb-16" data-id="element-1119">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1120">
                Key Features for Freight Operations
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1121">
                Our AI agents come with specialized capabilities designed
                specifically for freight companies.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1122">
              <FeatureCard title="Automated Load Matching" description="AI agents automatically match available trucks with pending shipments, optimizing capacity utilization and reducing empty miles." data-id="element-1123" />
              <FeatureCard title="Real-time Tracking Updates" description="Provide customers with automated, real-time updates on shipment status through their preferred communication channels." data-id="element-1124" />
              <FeatureCard title="Document Processing" description="Extract and process information from bills of lading, proof of delivery, and other freight documents with high accuracy." data-id="element-1125" />
              <FeatureCard title="Exception Management" description="Proactively identify and respond to delays, weather issues, or other exceptions that may impact deliveries." data-id="element-1126" />
              <FeatureCard title="Customer Communication" description="Automated responses to common inquiries about shipment status, delivery times, and documentation." data-id="element-1127" />
              <FeatureCard title="Analytics & Reporting" description="Generate insights on performance metrics, on-time delivery rates, and other KPIs to continuously improve operations." data-id="element-1128" />
            </div>
          </div>
        </div>
        {/* Integrations */}
        <div className="py-16 md:py-24 bg-white" data-id="element-1129">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1130">
            <div className="text-center mb-16" data-id="element-1131">
              <h2 className="text-3xl font-bold mb-4" data-id="element-1132">Seamless Integrations</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-1133">
                Our platform connects with the tools and software you already
                use in your freight operations.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-1134">
              {['SAP TM', 'Oracle TMS', 'MercuryGate', 'BluJay', 'Descartes', 'JDA', 'Manhattan', 'Trimble', 'McLeod', 'Kuebix', 'FourKites', 'project44'].map((integration, index) => <div key={index} className="bg-gray-50 p-4 rounded-lg text-center" data-id="element-1135">
                  <span className="font-medium" data-id="element-1136">{integration}</span>
                </div>)}
            </div>
          </div>
        </div>
        {/* CTA Section */}
        <div className="bg-black text-white py-16" data-id="element-1137">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1138">
            <h2 className="text-3xl font-bold mb-6" data-id="element-1139">
              Ready to transform your freight operations?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-id="element-1140">
              Schedule a personalized demo to see how Agentter.ai can automate
              and optimize your specific freight workflows.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-1141">
              <button onClick={() => setIsSalesFormOpen(true)} className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors" data-id="element-1142">
                Request a Demo
              </button>
              <a href="#" className="border border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors" data-id="element-1143">
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-1144" />
      {/* Sales Form Modal */}
      <SalesForm isOpen={isSalesFormOpen} onClose={() => setIsSalesFormOpen(false)} agentType="Freight Solutions" data-id="element-1145" />
    </div>;
}
function BenefitCard({
  icon,
  title,
  description
}) {
  return <div className="bg-gray-50 p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1146">
      <div className="bg-black text-white p-3 rounded-lg inline-block mb-4" data-id="element-1147">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-1148">{title}</h3>
      <p className="text-gray-600" data-id="element-1149">{description}</p>
    </div>;
}
function StepCard({
  number,
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1150">
      <div className="bg-black text-white w-10 h-10 rounded-full flex items-center justify-center mb-4 font-bold" data-id="element-1151">
        {number}
      </div>
      <h3 className="text-xl font-semibold mb-2" data-id="element-1152">{title}</h3>
      <p className="text-gray-600" data-id="element-1153">{description}</p>
    </div>;
}
function ResultItem({
  text
}) {
  return <div className="flex items-start" data-id="element-1154">
      <CheckCircle className="text-green-500 mr-2 mt-1 flex-shrink-0" size={20} data-id="element-1155" />
      <span data-id="element-1156">{text}</span>
    </div>;
}
function FeatureCard({
  title,
  description
}) {
  return <div className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-shadow" data-id="element-1157">
      <h3 className="text-xl font-semibold mb-3" data-id="element-1158">{title}</h3>
      <p className="text-gray-600" data-id="element-1159">{description}</p>
    </div>;
}